import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Menu, X, LogOut, LayoutDashboard, Plus, ShieldCheck, TrendingUp, Scale, BookOpen, MapPin, ChevronDown } from 'lucide-react';
import { CameroonLogo } from './Logo';
import { LanguageSelector } from './LanguageSelector';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

interface HeaderProps {
  currentUser?: { name: string; email: string; role: string } | null;
  onSignOut?: () => void;
  onNotificationClick?: (item: any) => void;
}

export const Header: React.FC<HeaderProps> = ({
  currentUser,
  onSignOut,
  onNotificationClick,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  const pathLower = (location.pathname || '/').toLowerCase();
  const isFr =
    pathLower === '/fr' || pathLower.startsWith('/fr/')
      ? true
      : pathLower === '/en' || pathLower.startsWith('/en/')
      ? false
      : language === 'fr';
  const { userProfile, isSuperAdmin, signOut: authSignOut, loading } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCitiesOpen, setMobileCitiesOpen] = useState(false);

  const effectiveUser = React.useMemo(() => {
    if (userProfile) {
      const name =
        (userProfile.fullName && userProfile.fullName.trim()) ||
        (userProfile.email && userProfile.email.includes('@') ? userProfile.email.split('@')[0] : '') ||
        'User';
      return {
        name,
        email: userProfile.email || '',
        role: userProfile.role || 'Buyer',
      };
    }
    if (currentUser) {
      const name =
        (currentUser.name && currentUser.name.trim()) ||
        (currentUser.email && currentUser.email.includes('@') ? currentUser.email.split('@')[0] : '') ||
        'User';
      return {
        name,
        email: currentUser.email || '',
        role: currentUser.role || 'Buyer',
      };
    }
    return null;
  }, [userProfile, currentUser]);

  // Close mobile drawer when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/' || location.pathname === '/fr' || location.pathname === '/en';
    return (
      location.pathname.startsWith(path) ||
      location.pathname.startsWith(`/fr${path}`) ||
      location.pathname.startsWith(`/en${path}`)
    );
  };

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-slate-200/90 shadow-2xs">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        {/* Left: Branding */}
        <div className="flex items-center gap-6">
          <Link
            to={isFr ? '/fr' : '/en'}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center cursor-pointer transition-transform duration-150 active:scale-95 focus:outline-hidden"
            aria-label="Cameroon Land Market - Home"
          >
            <CameroonLogo />
          </Link>
        </div>

        {/* Center: Desktop Navigation Links (Clean, direct, no mega menu popup) */}
        <nav className="hidden lg:flex items-center gap-3.5 xl:gap-5 relative whitespace-nowrap">
          <Link
            to={isFr ? '/fr' : '/en'}
            className={`text-[14px] xl:text-[15px] font-medium transition-colors tracking-tight ${
              isActive('/') ? 'text-[#094426] font-bold' : 'text-slate-700 hover:text-[#094426]'
            }`}
          >
            {t.home}
          </Link>

          <Link
            to={isFr ? '/fr/biens-a-vendre' : '/en/properties-for-sale'}
            className={`text-[14px] xl:text-[15px] font-medium transition-colors tracking-tight ${
              isActive('/properties-for-sale') || location.pathname.startsWith('/fr/biens-a-vendre') || location.pathname.startsWith('/en/properties-for-sale')
                ? 'text-[#094426] font-bold'
                : 'text-slate-700 hover:text-[#094426]'
            }`}
          >
            {t.buy}
          </Link>

          <Link
            to={isFr ? '/fr/biens-a-louer' : '/en/properties-for-rent'}
            className={`text-[14px] xl:text-[15px] font-medium transition-colors tracking-tight ${
              isActive('/properties-for-rent') || location.pathname.startsWith('/fr/biens-a-louer') || location.pathname.startsWith('/en/properties-for-rent')
                ? 'text-[#094426] font-bold'
                : 'text-slate-700 hover:text-[#094426]'
            }`}
          >
            {t.rent}
          </Link>

          <Link
            to={isFr ? '/fr/terrains-a-vendre' : '/en/land-for-sale'}
            className={`text-[14px] xl:text-[15px] font-medium transition-colors tracking-tight ${
              location.pathname.includes('terrains') || location.pathname.includes('land')
                ? 'text-[#094426] font-bold'
                : 'text-slate-700 hover:text-[#094426]'
            }`}
          >
            {isFr ? 'Terrains' : 'Land'}
          </Link>

          <Link
            to={isFr ? '/fr/professionnels' : '/en/professionals'}
            className={`text-[14px] xl:text-[15px] font-medium transition-colors tracking-tight ${
              isActive('/professionals') || location.pathname.startsWith('/fr/professionnels') || location.pathname.startsWith('/en/professionals')
                ? 'text-[#094426] font-bold'
                : 'text-slate-700 hover:text-[#094426]'
            }`}
          >
            {t.professionals}
          </Link>

          <Link
            to={isFr ? '/fr/deposer-annonce' : '/en/post-property'}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#094426] hover:bg-[#07361e] text-white text-[13px] font-semibold transition-all shadow-2xs hover:shadow-xs active:scale-95 shrink-0 ml-1"
          >
            <Plus className="w-3.5 h-3.5 stroke-[2.5]" />
            <span>{isFr ? 'Déposer Annonce' : 'Post Free Ad'}</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-400 text-slate-950 font-black tracking-wider uppercase ml-0.5">
              {isFr ? 'Gratuit' : 'Free'}
            </span>
          </Link>
        </nav>

        {/* Right Side Controls */}
        <div className="hidden lg:flex items-center gap-2.5 xl:gap-3">
          {/* Modern Bilingual Language Selector */}
          <LanguageSelector variant="header" />

          {loading && !effectiveUser ? (
            <div className="flex items-center gap-2 pl-2 border-l border-slate-200">
              <div className="w-8 h-8 rounded-full bg-slate-200/80 animate-pulse"></div>
              <div className="h-4 w-16 bg-slate-200/80 rounded animate-pulse"></div>
            </div>
          ) : effectiveUser ? (
            <div className="flex items-center gap-2.5 pl-2 border-l border-slate-200">
              <Link
                to={isFr ? '/fr/tableau-de-bord' : '/en/dashboard'}
                className={`text-xs font-bold px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors ${
                  location.pathname === '/dashboard' || location.pathname === '/fr/tableau-de-bord' || location.pathname === '/en/dashboard' || location.pathname === '/account'
                    ? 'bg-[#094426] text-white shadow-2xs'
                    : 'text-[#094426] bg-emerald-50 hover:bg-emerald-100 border border-emerald-300'
                }`}
                title="User Account Dashboard"
              >
                <LayoutDashboard className="w-3.5 h-3.5" />
                <span>{t.dashboard}</span>
              </Link>
              <Link
                to={isFr ? '/fr/mon-profil' : '/en/profile'}
                className="flex items-center gap-2 hover:bg-slate-50 p-1 rounded-xl transition-colors cursor-pointer group"
                title="View & Edit Profile"
              >
                <div className="w-8 h-8 rounded-full bg-[#094426] text-white flex items-center justify-center text-xs font-bold">
                  {(effectiveUser.name?.charAt(0) || 'U').toUpperCase()}
                </div>
                <div className="text-left leading-tight">
                  <div className="text-xs font-bold text-slate-900 group-hover:text-[#094426] transition-colors truncate max-w-[120px]">
                    {effectiveUser.name || 'User'}
                  </div>
                  <div className="text-[10px] text-emerald-700 font-semibold">
                    {effectiveUser.role || 'Buyer'}
                  </div>
                </div>
              </Link>
              <button
                type="button"
                onClick={async () => {
                  await authSignOut();
                  onSignOut?.();
                }}
                title={t.signOut}
                className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg transition-colors cursor-pointer"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              {/* Sign In Button */}
              <Link
                to={isFr ? '/fr/connexion' : '/en/signin'}
                className="text-sm font-semibold text-slate-700 hover:text-[#094426] px-3.5 py-2 rounded-xl hover:bg-slate-50 transition-colors"
              >
                {isFr ? 'Connexion' : 'Sign In'}
              </Link>

              {/* Sign Up Button */}
              <Link
                to={isFr ? '/fr/inscription' : '/en/signup'}
                className="text-sm font-bold text-slate-900 bg-slate-100 hover:bg-slate-200/80 border border-slate-200 px-4 py-2 rounded-xl transition-colors"
              >
                {isFr ? 'S’inscrire' : 'Sign Up'}
              </Link>
            </>
          )}
        </div>

        {/* Mobile menu toggle */}
        <div className="lg:hidden flex items-center gap-2">
          <LanguageSelector variant="header" />

          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 cursor-pointer"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white/98 backdrop-blur-md border-b border-slate-200/90 px-4 py-5 space-y-3 shadow-xl max-h-[85vh] overflow-y-auto animate-in slide-in-from-top-2 duration-200">
          {/* Integrated Language Switcher Bar in Mobile Drawer */}
          <LanguageSelector variant="mobile" />

          <div className="flex flex-col space-y-2 text-left">
            <Link
              to={isFr ? '/fr' : '/en'}
              onClick={() => setMobileMenuOpen(false)}
              className="py-2.5 text-sm font-semibold text-slate-800 border-b border-slate-100"
            >
              {t.home}
            </Link>

            <Link
              to={isFr ? '/fr/biens-a-vendre' : '/en/properties-for-sale'}
              onClick={() => setMobileMenuOpen(false)}
              className="py-2.5 text-sm font-medium text-slate-700 border-b border-slate-100"
            >
              {t.buy}
            </Link>

            <Link
              to={isFr ? '/fr/biens-a-louer' : '/en/properties-for-rent'}
              onClick={() => setMobileMenuOpen(false)}
              className="py-2.5 text-sm font-medium text-slate-700 border-b border-slate-100"
            >
              {t.rent}
            </Link>

            <Link
              to={isFr ? '/fr/terrains-a-vendre' : '/en/land-for-sale'}
              onClick={() => setMobileMenuOpen(false)}
              className="py-2.5 text-sm font-medium text-slate-700 border-b border-slate-100"
            >
              {isFr ? 'Terrains à Vendre' : 'Land for Sale'}
            </Link>

            {/* All 10 Cameroon Regions & Cities Mobile Accordion */}
            <div className="border-b border-slate-100 pb-1">
              <button
                type="button"
                onClick={() => setMobileCitiesOpen(!mobileCitiesOpen)}
                className="w-full py-2.5 text-sm font-medium text-slate-700 flex items-center justify-between cursor-pointer"
              >
                <span className="flex items-center gap-1.5 font-semibold text-slate-800">
                  <MapPin className="w-4 h-4 text-emerald-700" />
                  <span>{isFr ? 'Les 10 Régions & Villes' : 'All 10 Regions & Cities'}</span>
                </span>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full font-bold">
                    10 Régions
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${mobileCitiesOpen ? 'rotate-180' : ''}`} />
                </div>
              </button>

              {mobileCitiesOpen && (
                <div className="bg-slate-50 p-3 rounded-2xl my-2 space-y-2 border border-slate-200 animate-in fade-in duration-200">
                  <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                    {isFr ? 'Sélectionner une région ou ville :' : 'Browse by Cameroon Region:'}
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 text-xs font-semibold">
                    <Link
                      to={isFr ? '/fr/villes/douala' : '/en/cities/douala'}
                      onClick={() => setMobileMenuOpen(false)}
                      className="p-2 rounded-xl bg-white border border-slate-200 text-slate-800 hover:border-emerald-600 flex items-center gap-1.5"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0" />
                      <span>Douala (Littoral)</span>
                    </Link>
                    <Link
                      to={isFr ? '/fr/villes/yaounde' : '/en/cities/yaounde'}
                      onClick={() => setMobileMenuOpen(false)}
                      className="p-2 rounded-xl bg-white border border-slate-200 text-slate-800 hover:border-emerald-600 flex items-center gap-1.5"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0" />
                      <span>Yaoundé (Centre)</span>
                    </Link>
                    <Link
                      to={isFr ? '/fr/villes/kribi' : '/en/cities/kribi'}
                      onClick={() => setMobileMenuOpen(false)}
                      className="p-2 rounded-xl bg-white border border-slate-200 text-slate-800 hover:border-emerald-600 flex items-center gap-1.5"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0" />
                      <span>Kribi (Sud)</span>
                    </Link>
                    <Link
                      to={isFr ? '/fr/villes/buea' : '/en/cities/buea'}
                      onClick={() => setMobileMenuOpen(false)}
                      className="p-2 rounded-xl bg-white border border-slate-200 text-slate-800 hover:border-emerald-600 flex items-center gap-1.5"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0" />
                      <span>Buea (Sud-Ouest)</span>
                    </Link>
                    <Link
                      to={isFr ? '/fr/villes/limbe' : '/en/cities/limbe'}
                      onClick={() => setMobileMenuOpen(false)}
                      className="p-2 rounded-xl bg-white border border-slate-200 text-slate-800 hover:border-emerald-600 flex items-center gap-1.5"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0" />
                      <span>Limbe (Sud-Ouest)</span>
                    </Link>
                    <Link
                      to={isFr ? '/fr/villes/bafoussam' : '/en/cities/bafoussam'}
                      onClick={() => setMobileMenuOpen(false)}
                      className="p-2 rounded-xl bg-white border border-slate-200 text-slate-800 hover:border-emerald-600 flex items-center gap-1.5"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0" />
                      <span>Bafoussam (Ouest)</span>
                    </Link>
                    <Link
                      to={isFr ? '/fr/villes/bamenda' : '/en/cities/bamenda'}
                      onClick={() => setMobileMenuOpen(false)}
                      className="p-2 rounded-xl bg-white border border-slate-200 text-slate-800 hover:border-emerald-600 flex items-center gap-1.5"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0" />
                      <span>Bamenda (Nord-Ouest)</span>
                    </Link>
                    <Link
                      to={isFr ? '/fr/villes/garoua' : '/en/cities/garoua'}
                      onClick={() => setMobileMenuOpen(false)}
                      className="p-2 rounded-xl bg-white border border-slate-200 text-slate-800 hover:border-emerald-600 flex items-center gap-1.5"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0" />
                      <span>Garoua (Nord)</span>
                    </Link>
                    <Link
                      to={isFr ? '/fr/villes/maroua' : '/en/cities/maroua'}
                      onClick={() => setMobileMenuOpen(false)}
                      className="p-2 rounded-xl bg-white border border-slate-200 text-slate-800 hover:border-emerald-600 flex items-center gap-1.5"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0" />
                      <span>Maroua (Extrême-Nord)</span>
                    </Link>
                    <Link
                      to={isFr ? '/fr/villes/ngaoundere' : '/en/cities/ngaoundere'}
                      onClick={() => setMobileMenuOpen(false)}
                      className="p-2 rounded-xl bg-white border border-slate-200 text-slate-800 hover:border-emerald-600 flex items-center gap-1.5"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0" />
                      <span>Ngaoundéré (Adamaoua)</span>
                    </Link>
                    <Link
                      to={isFr ? '/fr/villes/bertoua' : '/en/cities/bertoua'}
                      onClick={() => setMobileMenuOpen(false)}
                      className="p-2 rounded-xl bg-white border border-slate-200 text-slate-800 hover:border-emerald-600 flex items-center gap-1.5"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0" />
                      <span>Bertoua (Est)</span>
                    </Link>
                    <Link
                      to={isFr ? '/fr/villes/ebolowa' : '/en/cities/ebolowa'}
                      onClick={() => setMobileMenuOpen(false)}
                      className="p-2 rounded-xl bg-white border border-slate-200 text-slate-800 hover:border-emerald-600 flex items-center gap-1.5"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0" />
                      <span>Ebolowa (Sud)</span>
                    </Link>
                  </div>
                  <div className="pt-1.5">
                    <Link
                      to={isFr ? '/fr/villes' : '/en/cities'}
                      onClick={() => setMobileMenuOpen(false)}
                      className="w-full text-center py-2 rounded-xl bg-[#094426] text-white text-xs font-bold block"
                    >
                      {isFr ? 'Voir toutes les 479 localités du Cameroun →' : 'Browse all 479 Cameroon towns →'}
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <Link
              to={isFr ? '/fr/verifier-propriete' : '/en/verify-property'}
              onClick={() => setMobileMenuOpen(false)}
              className="py-2.5 text-sm font-medium text-slate-700 border-b border-slate-100 flex items-center justify-between"
            >
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-emerald-700" />
                <span>{isFr ? 'Vérifier Titre Foncier' : 'Verify Titre Foncier'}</span>
              </span>
              <span className="text-[10px] bg-emerald-50 text-[#094426] px-2 py-0.5 rounded-full font-bold border border-emerald-100">
                MINDCAF
              </span>
            </Link>

            <Link
              to={isFr ? '/fr/deposer-annonce' : '/en/post-property'}
              onClick={() => setMobileMenuOpen(false)}
              className="py-3 px-4 bg-slate-950 hover:bg-slate-900 text-white font-bold rounded-2xl shadow-sm flex items-center justify-between my-2"
            >
              <span className="flex items-center gap-2 text-xs">
                <Plus className="w-4 h-4 text-amber-400" />
                <span>{isFr ? 'Déposer une Annonce' : 'Post Free Ad'}</span>
              </span>
              <span className="text-[10px] bg-amber-400 text-slate-950 px-2.5 py-0.5 rounded-full font-black uppercase tracking-wider shadow-2xs">
                {isFr ? '100% Gratuit' : '100% Free'}
              </span>
            </Link>

            <Link
              to={isFr ? '/fr/professionnels' : '/en/professionals'}
              onClick={() => setMobileMenuOpen(false)}
              className="py-2.5 text-sm font-medium text-slate-700 border-b border-slate-100 flex items-center justify-between"
            >
              <span>{t.professionals}</span>
              <span className="text-[10px] text-slate-500 font-semibold">{isFr ? 'Notaires & Géomètres' : 'Notaries & Surveyors'}</span>
            </Link>

            <Link
              to={isFr ? '/fr/ressources/frais-achat-terrain-et-taxes' : '/en/resources/cost-of-buying-land-and-taxes'}
              onClick={() => setMobileMenuOpen(false)}
              className="py-2.5 text-sm font-medium text-slate-700 border-b border-slate-100 flex items-center justify-between"
            >
              <span className="flex items-center gap-1.5">
                <Scale className="w-4 h-4 text-emerald-700" />
                <span>{isFr ? 'Simulateur Frais Notariés' : 'Notary Fee Estimator'}</span>
              </span>
              <span className="text-[10px] bg-amber-100 text-amber-900 px-2 py-0.5 rounded-full font-bold">
                {isFr ? 'Calculateur' : 'Calculator'}
              </span>
            </Link>

            <Link
              to={isFr ? '/fr/ressources' : '/en/resources'}
              onClick={() => setMobileMenuOpen(false)}
              className="py-2.5 text-sm font-medium text-slate-700 border-b border-slate-100 flex items-center justify-between"
            >
              <span className="flex items-center gap-1.5">
                <BookOpen className="w-4 h-4 text-slate-600" />
                <span>{isFr ? 'Guides & Textes de Lois' : 'Legal Guides & Laws'}</span>
              </span>
              <span className="text-[10px] text-slate-400">9 {isFr ? 'dossiers' : 'guides'}</span>
            </Link>

            <Link
              to={isFr ? '/fr/tableau-de-bord' : '/en/dashboard'}
              onClick={() => setMobileMenuOpen(false)}
              className="py-2 text-sm font-semibold text-[#094426] flex items-center justify-between border-b border-slate-100"
            >
              <span className="flex items-center gap-2">
                <LayoutDashboard className="w-4 h-4" />
                <span>{t.dashboard}</span>
              </span>
              <span className="text-[10px] bg-emerald-100 text-[#094426] px-2 py-0.5 rounded-full font-bold">
                Account
              </span>
            </Link>

            {effectiveUser && (
              <Link
                to={isFr ? '/fr/statistiques-vendeur' : '/en/seller-analytics'}
                onClick={() => setMobileMenuOpen(false)}
                className="py-2 text-sm font-semibold text-[#094426] flex items-center justify-between border-b border-slate-100"
              >
                <span className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                  <span>{isFr ? 'Statistiques Vendeur' : 'Seller Analytics'}</span>
                </span>
                <span className="text-[10px] bg-emerald-50 text-[#094426] px-2 py-0.5 rounded-full font-bold border border-emerald-200">
                  Stats
                </span>
              </Link>
            )}

            {effectiveUser && (
              <Link
                to={isFr ? '/fr/mon-profil' : '/en/profile'}
                onClick={() => setMobileMenuOpen(false)}
                className="py-2 text-sm font-medium text-[#094426] flex items-center justify-between border-t border-slate-100"
              >
                <span>{isFr ? 'Mon Profil' : 'My Profile'} ({effectiveUser.role || 'Member'})</span>
                <span className="text-[10px] bg-emerald-50 text-[#094426] px-2 py-0.5 rounded-full font-bold border border-emerald-200">
                  Settings
                </span>
              </Link>
            )}
          </div>

          <div className="pt-2 flex gap-3">
            {loading && !effectiveUser ? (
              <div className="w-full h-10 bg-slate-100 rounded-xl animate-pulse"></div>
            ) : effectiveUser ? (
              <button
                type="button"
                onClick={async () => {
                  await authSignOut();
                  onSignOut?.();
                  setMobileMenuOpen(false);
                }}
                className="w-full py-2.5 text-sm font-semibold border border-red-200 text-red-700 rounded-xl hover:bg-red-50 cursor-pointer"
              >
                {t.signOut} ({effectiveUser.name || 'User'})
              </button>
            ) : (
              <>
                <Link
                  to={isFr ? '/fr/connexion' : '/en/signin'}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex-1 py-2.5 text-center text-xs font-bold border border-slate-300 rounded-xl hover:bg-slate-50 text-slate-700 shadow-2xs"
                >
                  {t.signIn}
                </Link>
                <Link
                  to={isFr ? '/fr/inscription' : '/en/signup'}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex-1 py-2.5 text-center text-xs font-black bg-amber-400 hover:bg-amber-300 text-slate-950 rounded-xl shadow-xs"
                >
                  {t.signUp}
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
